import React from 'react';
import { FileText, Clock, CheckCircle, Shield } from 'lucide-react';
import { StorytellingLayout, ExpandableCard } from '../base';

/**
 * Tela de storytelling para Passaporte
 * 
 * Copy focada em: resolver burocracia, tranquilidade, DSC cuida de tudo.
 * Sem frases motivacionais genéricas.
 * 
 * Schema esperado (details):
 * - service, validity, processing_time
 * - includes: string[]
 */

const DEFAULT_NARRATIVE = 'Nada de filas, formulários confusos ou idas desnecessárias à Polícia Federal. A DSC Travel resolve cada etapa da emissão do seu passaporte — você só precisa comparecer no dia agendado.';

export default function StorytellingPassport({
  block,
  stepIndicator,
  narrativeText,
  ctaText,
  onContinue,
  onBack,
  onShare
}) {
  const details = block.details || {};
  const includes = details.includes || [];

  const backgroundImage = 'https://images.unsplash.com/photo-1452421822248-d4c2b47f0c81?w=800&q=80';

  const formatPrice = (value) => {
    if (!value && value !== 0) return '';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const cardContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Ícone + Título */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: 'rgba(93, 212, 195, 0.15)',
          borderRadius: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <FileText size={24} color="#5DD4C3" strokeWidth={2} />
        </div>
        <div>
          <p style={{
            fontSize: '1.0625rem',
            fontWeight: 600,
            color: 'white',
            margin: 0,
            lineHeight: 1.3
          }}>
            {block.title || 'Emissão de Passaporte'}
          </p>
          <p style={{
            fontSize: '0.8125rem',
            color: 'rgba(255, 255, 255, 0.6)',
            margin: '0.25rem 0 0 0'
          }}>
            Serviço completo — DSC cuida de tudo
          </p>
        </div>
      </div>

      {/* Destaque: preço tudo incluso */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.875rem',
        backgroundColor: 'rgba(93, 212, 195, 0.1)',
        borderRadius: '0.75rem',
        borderLeft: '3px solid #5DD4C3'
      }}>
        <div style={{
          width: '44px', height: '44px',
          backgroundColor: 'rgba(93, 212, 195, 0.15)',
          borderRadius: '0.5rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0
        }}>
          <Shield size={20} color="#5DD4C3" strokeWidth={2} />
        </div>
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#5DD4C3', margin: 0 }}>
            {block.price ? formatPrice(block.price) : 'R$ 407,25'} — tudo incluso
          </p>
          <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: '0.125rem 0 0 0' }}>
            Taxa federal + serviço DSC, sem custo adicional
          </p>
        </div>
      </div>

      {/* Grid: Validade / Prazo / Inclusos */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px', height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <FileText size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.validity || '10 anos'}
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Validade
          </p>
        </div>

        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px', height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Clock size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.processing_time || '6 a 8 semanas'}
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Prazo estimado
          </p>
        </div>

        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px', height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <CheckCircle size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {includes.length} itens
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Inclusos
          </p>
        </div>
      </div>

      {/* Preview inclusos */}
      {includes.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          {includes.slice(0, 3).map((item, idx) => (
            <div key={idx} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              backgroundColor: 'rgba(93, 212, 195, 0.15)',
              borderRadius: '0.5rem'
            }}>
              <CheckCircle size={14} color="#5DD4C3" strokeWidth={2} />
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#5DD4C3' }}>
                {item}
              </span>
            </div>
          ))}
          {includes.length > 3 && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.5rem 0.75rem',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '0.5rem'
            }}>
              <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                +{includes.length - 3} itens
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const expandedContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {includes.length > 0 && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.5rem 0' }}>
            O que está incluso
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {includes.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
              }}>
                <CheckCircle size={14} color="#5DD4C3" strokeWidth={2} />
                <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <StorytellingLayout
      backgroundImage={backgroundImage}
      title="Passaporte Brasileiro"
      narrativeText={narrativeText || DEFAULT_NARRATIVE}
      stepIndicator={stepIndicator}
      ctaText={ctaText}
      onContinue={onContinue}
      onBack={onBack}
      onShare={onShare}
    >
      <ExpandableCard expandedContent={expandedContent}>
        {cardContent}
      </ExpandableCard>
    </StorytellingLayout>
  );
}
